
import { Component, Vue } from "vue-property-decorator";
import ProjectsRepository from "../repositories/implementations/ProjectsRepository";
import ProjectLanguagesRepository from "../repositories/implementations/ProjectLanguagesRepository";
import { Project } from "../entities/Project";
import { toastSuccess, toastError } from "../utils/toast";
import { getSupportedLanguages, getLanguageLabel } from "../utils/languageUtils";
import LanguageDropdown from "../components/LanguageDropdown.vue";

@Component({ components: { LanguageDropdown } })
export default class ProjectsView extends Vue {
  projects: Project[] = [];

  loading = true;

  supportedLanguages = getSupportedLanguages();

  projectsRepo = new ProjectsRepository();

  langRepo = new ProjectLanguagesRepository();

  createModal = {
    open: false,
    name: "",
    sourceLanguage: "",
    nameError: "",
    langError: "",
    loading: false,
  };

  editModal = {
    open: false,
    projectId: "",
    name: "",
    sourceLanguage: "",
    languages: [] as { value: string; text: string }[],
    nameError: "",
    langError: "",
    loading: false,
  };

  deleteModal = {
    open: false,
    project: null as Project | null,
    confirmation: "",
    error: "",
    loading: false,
  };

  async created() {
    await this.loadProjects();
  }

  async loadProjects() {
    this.loading = true;
    try {
      this.projects = await this.projectsRepo.getAll();
    } catch (e: any) {
      toastError("Failed to load projects.");
    } finally {
      this.loading = false;
    }
  }

  goToProject(id: string) {
    this.$router.push({ name: "project-languages", params: { projectId: id } });
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }

  // ── Create ──────────────────────────────────────────────

  openCreateModal() {
    this.createModal = { open: true, name: "", sourceLanguage: "", nameError: "", langError: "", loading: false };
  }

  closeCreateModal() {
    this.createModal.open = false;
  }

  async submitCreate() {
    let valid = true;
    if (!this.createModal.name.trim()) {
      this.createModal.nameError = "Project name is required.";
      valid = false;
    }
    if (!this.createModal.sourceLanguage.trim()) {
      this.createModal.langError = "Source language code is required.";
      valid = false;
    }
    if (!valid) return;

    this.createModal.loading = true;
    try {
      await this.projectsRepo.create(this.createModal.name, this.createModal.sourceLanguage);
      toastSuccess("Project created.");
      this.createModal.open = false;
      await this.loadProjects();
    } catch (e: any) {
      toastError(e.message || "Failed to create project.");
    } finally {
      this.createModal.loading = false;
    }
  }

  // ── Edit ────────────────────────────────────────────────

  async openEditModal(project: Project) {
    const langs = await this.langRepo.getByProject(project.id);
    this.editModal = {
      open: true,
      projectId: project.id,
      name: project.name,
      sourceLanguage: project.sourceLanguageCode,
      languages: langs.map((l) => ({ value: l.languageCode, text: getLanguageLabel(l.languageCode) })),
      nameError: "",
      langError: "",
      loading: false,
    };
  }

  closeEditModal() {
    this.editModal.open = false;
  }

  async submitEdit() {
    let valid = true;
    if (!this.editModal.name.trim()) {
      this.editModal.nameError = "Project name is required.";
      valid = false;
    }
    if (!this.editModal.sourceLanguage) {
      this.editModal.langError = "Source language is required.";
      valid = false;
    }
    if (!valid) return;

    this.editModal.loading = true;
    try {
      await this.projectsRepo.update(this.editModal.projectId, this.editModal.name, this.editModal.sourceLanguage);
      toastSuccess("Project updated.");
      this.editModal.open = false;
      await this.loadProjects();
    } catch (e: any) {
      toastError(e.message || "Failed to update project.");
    } finally {
      this.editModal.loading = false;
    }
  }

  // ── Delete ──────────────────────────────────────────────

  openDeleteModal(project: Project) {
    this.deleteModal = { open: true, project, confirmation: "", error: "", loading: false };
  }

  closeDeleteModal() {
    this.deleteModal.open = false;
  }

  async submitDelete() {
    if (this.deleteModal.confirmation !== "DELETE") {
      this.deleteModal.error = 'Please type "DELETE" to confirm.';
      return;
    }
    this.deleteModal.loading = true;
    try {
      await this.projectsRepo.softDelete((this.deleteModal.project as any).id);
      toastSuccess("Project deleted.");
      this.deleteModal.open = false;
      await this.loadProjects();
    } catch (e: any) {
      toastError(e.message || "Failed to delete project.");
    } finally {
      this.deleteModal.loading = false;
    }
  }
}
